/**
 * REST path constants. Single source of truth for both backend controllers
 * and frontend API client. Never hardcode paths in either place — import
 * from here.
 *
 * URL namespace split:
 *   - `/api/auth/*`  /api/sales/*  /api/admin/*  — JSON API (backend)
 *   - `/health/*`  /metrics                      — infra (backend, K8s/Prom)
 *   - everything else                            — React SPA (Vite / static)
 *
 * Why `/api` prefix: SPA client-side routes (e.g. React Router
 * `/admin/accounts`) MUST NOT collide with backend API routes when both are
 * served from the same domain. Without the prefix, an F5 refresh on
 * `/admin/accounts` is routed by Caddy to the backend, which returns 401
 * JSON instead of the SPA HTML.
 *
 * Convention:
 *   - `/api/auth/*` — unauthenticated or session-issuance endpoints
 *   - `/api/sales/*` — JWT-required, role: sales | lead | admin
 *   - `/api/admin/*` — JWT-required, role: admin only
 *   - `/health/*`    — unauthenticated infra endpoints (no /api prefix)
 *   - `/metrics`     — Prometheus scrape target (no /api prefix)
 */
const API = '/api';
export const Routes = {
    auth: {
        login: `${API}/auth/login`,
        me: `${API}/auth/me`,
        logout: `${API}/auth/logout`,
    },
    sales: {
        leads: `${API}/sales/leads`,
        /** Category / tag aggregations across leads visible to this sales rep. */
        leadTagStats: `${API}/sales/leads/tag-stats`,
        lead: (id) => `${API}/sales/leads/${id}`,
        leadClaim: (id) => `${API}/sales/leads/${id}/claim`,
        leadRelease: (id) => `${API}/sales/leads/${id}/release`,
        leadClose: (id) => `${API}/sales/leads/${id}/close`,
        leadMessages: (id) => `${API}/sales/leads/${id}/messages`,
        /** Conversation between the outbound account and this lead's target. */
        leadConversation: (id) => `${API}/sales/leads/${id}/conversation`,
        /** POST a sales-authored reply that goes out via the same TG account. */
        leadConversationReply: (id) => `${API}/sales/leads/${id}/conversation/reply`,
        /** Expanded trigger raw_messages for the lead detail panel. */
        leadTriggers: (id) => `${API}/sales/leads/${id}/triggers`,
        notifications: `${API}/sales/notifications`,
        notificationsAck: `${API}/sales/notifications/ack`,
    },
    admin: {
        proxies: `${API}/admin/proxies`,
        proxy: (id) => `${API}/admin/proxies/${id}`,
        proxyHealthCheck: (id) => `${API}/admin/proxies/${id}/health-check`,
        proxiesBulkImport: `${API}/admin/proxies/bulk-import`,
        accounts: `${API}/admin/accounts`,
        account: (id) => `${API}/admin/accounts/${id}`,
        accountWarmup: (id) => `${API}/admin/accounts/${id}/warmup`,
        /** Operator manual pause: evicts mtcute handle and marks reconcile to
         *  skip this account until /resume is called. */
        accountPause: (id) => `${API}/admin/accounts/${id}/pause`,
        /** Clears the pause flag — next reconcile (≤30s) re-spawns. */
        accountResume: (id) => `${API}/admin/accounts/${id}/resume`,
        /** Re-fetch TG profile (username/first/last name) via getMe. Admin button. */
        accountRefreshProfile: (id) => `${API}/admin/accounts/${id}/refresh-profile`,
        accountsLoginStart: `${API}/admin/accounts/login/start`,
        accountsLoginCode: `${API}/admin/accounts/login/code`,
        accountsLoginTwoFa: `${API}/admin/accounts/login/2fa`,
        /**
         * Skip the SMS+code flow by submitting a pre-minted mtcute StringSession.
         * Useful when operator imports accounts from a prior tooling round.
         */
        accountsImportSession: `${API}/admin/accounts/import-session`,
        groups: `${API}/admin/groups`,
        group: (id) => `${API}/admin/groups/${id}`,
        /** Leave a joined group via the account that joined it (or an override). */
        groupLeave: (id) => `${API}/admin/groups/${id}/leave`,
        /** Aggregate stats per group (msg count last 24h + total, lead count). */
        groupStats: `${API}/admin/groups/stats`,
        tenantConfig: `${API}/admin/tenant-config`,
        tenantConfigVersion: (id) => `${API}/admin/tenant-config/versions/${id}`,
        /** Restore an old version by copying its fields into a new active version
         *  (version++). The current active gets marked rolled_back atomically. */
        tenantConfigVersionRestore: (id) => `${API}/admin/tenant-config/versions/${id}/restore`,
        /** Dry-run a sample message through the L0-L3 cascade without
         *  persisting a lead. Returns per-layer scores for admin debugging. */
        tenantConfigSimulate: `${API}/admin/tenant-config/simulate`,
        /** Multi-anchor noise filter management (L1 stage). */
        /** PMs from TG service accounts (777000 etc.) addressed to a managed
         *  account. Phase 1: capture-only; admin reads raw text. */
        accountSystemMessages: (id) => `${API}/admin/accounts/${id}/system-messages`,
        noiseAnchors: `${API}/admin/noise-anchors`,
        /** Bulk add (manual paste + AI accept-batch). */
        noiseAnchorsBulk: `${API}/admin/noise-anchors/bulk`,
        /** 14B-driven suggestions from uncovered samples. */
        noiseAnchorsSuggest: `${API}/admin/noise-anchors/suggest`,
        featureFlags: `${API}/admin/flags`,
        featureFlag: (key, scope) => `${API}/admin/flags/${encodeURIComponent(scope)}/${encodeURIComponent(key)}`,
        audit: `${API}/admin/audit`,
        /** Dashboard summary — counts + cost + stream lag. */
        dashboard: `${API}/admin/dashboard`,
        /** Per-day LLM usage rollup + per-purpose breakdown. */
        llmUsage: `${API}/admin/llm-usage`,
        /** Browse / search raw_messages (debug ingest). */
        rawMessages: `${API}/admin/raw-messages`,
        /** Admin-curated keyword filters that drop matching inbound messages. */
        filters: `${API}/admin/filters`,
        filter: (id) => `${API}/admin/filters/${id}`,
        /** In-memory recent-message samples for crafting new filter rules. */
        filterSamples: `${API}/admin/filters/samples`,
        /** Sales user CRUD — admin add/remove/edit sales reps + their passwords. */
        salesUsers: `${API}/admin/sales-users`,
        salesUser: (id) => `${API}/admin/sales-users/${id}`,
        /** Read-only view of detected leads (sales side handles claim/close). */
        leads: `${API}/admin/leads`,
        lead: (id) => `${API}/admin/leads/${id}`,
        /** Expanded trigger raw_messages for the admin lead inspector. */
        leadTriggers: (id) => `${API}/admin/leads/${id}/triggers`,
        /** Category / tag aggregations across tenant leads for the chip filter. */
        leadTagStats: `${API}/admin/leads/tag-stats`,
        /** System-wide infrastructure config (LLM endpoints / TG device / scheduler). */
        systemConfig: `${API}/admin/system-config`,
        systemConfigItem: (key) => `${API}/admin/system-config/${encodeURIComponent(key)}`,
        /** Smoke-test a layer's currently-saved (or operator-chosen) backend. */
        systemConfigTestLlm: `${API}/admin/system-config/test-llm`,
        /** Per-message cascade verdict log (debug / "what got let through L1"). */
        cascadeTraces: `${API}/admin/cascade-traces`,
        cascadeTraceMarkNoise: (id) => `${API}/admin/cascade-traces/${id}/mark-as-noise`,
        /** Resource discovery — bios / descriptions mined for channels + groups. */
        discoveryStats: `${API}/admin/discovery/stats`,
        discoveryChats: `${API}/admin/discovery/chats`,
        discoveryChat: (id) => `${API}/admin/discovery/chats/${id}`,
        /** Who referenced this chat (resource_links with targetChatId = id). */
        discoveryChatSources: (id) => `${API}/admin/discovery/chats/${id}/sources`,
        /** Force a (re-)resolve of one chat now (priority 0 job). */
        discoveryChatResolve: (id) => `${API}/admin/discovery/chats/${id}/resolve`,
        discoveryUsers: `${API}/admin/discovery/users`,
        discoveryUser: (id) => `${API}/admin/discovery/users/${id}`,
        discoveryJobs: `${API}/admin/discovery/jobs`,
        /** Backfill seeds (lead targets / member scans) + retry failed. */
        discoveryBackfill: `${API}/admin/discovery/backfill`,
        /** Group expansion: scored candidates → paced joins → evaluation. */
        expansionStats: `${API}/admin/expansion/stats`,
        expansionPlans: `${API}/admin/expansion/plans`,
        expansionPlan: (id) => `${API}/admin/expansion/plans/${id}`,
        /** approve / pin / reject / blacklist / leave / reset one plan. */
        expansionPlanDecide: (id) => `${API}/admin/expansion/plans/${id}/decide`,
        /** Create (or fetch) the plan for a discovered chat and apply a decision. */
        expansionDecideChat: (chatId) => `${API}/admin/expansion/chats/${chatId}/decide`,
        /** Run one scheduler tick now (score + promote + join + evaluate). */
        expansionRun: `${API}/admin/expansion/run`,
    },
    health: {
        liveness: '/health/liveness',
        readiness: '/health/readiness',
        metrics: '/metrics',
    },
};
//# sourceMappingURL=routes.js.map